import type { TrailStatusModel, TrailTrustModel } from "@/lib/trail-taxonomy";

/**
 * The small trust and status markers, shared by the card and the detail hero.
 *
 * Every one of these carries its meaning in words. Tone drives a border and a
 * dot, never the message — a reader who cannot distinguish the colours still
 * reads "Closed" or "Status not confirmed".
 */

const TONE_RING: Record<TrailStatusModel["tone"], string> = {
  positive: "border-[color-mix(in_srgb,var(--green)_55%,transparent)]",
  warning: "border-[color-mix(in_srgb,var(--cta)_60%,transparent)]",
  critical: "border-[color-mix(in_srgb,#c2584f_65%,transparent)]",
  neutral: "border-[var(--border-subtle)]",
};

const TONE_DOT: Record<TrailStatusModel["tone"], string> = {
  positive: "bg-[var(--green)]",
  warning: "bg-[var(--cta)]",
  critical: "bg-[#c2584f]",
  neutral: "bg-[var(--muted)]",
};

export function TrailStatusChip({ status }: { status: TrailStatusModel }) {
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[0.72rem] font-semibold tracking-wide text-[var(--foreground)] ${TONE_RING[status.tone]}`}
    >
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${TONE_DOT[status.tone]}`} aria-hidden="true" />
      {status.label}
    </span>
  );
}

/**
 * The trust strip. Renders nothing for a draft with no checks recorded —
 * an empty badge row would imply a verification that has not happened.
 */
export function TrailTrustBadges({
  trust,
  className = "",
}: {
  trust: TrailTrustModel;
  className?: string;
}) {
  if (trust.badges.length === 0) return null;
  return (
    <ul className={`m-0 flex list-none flex-wrap gap-2 p-0 ${className}`}>
      {trust.badges.map((badge) => (
        <li
          key={badge.key}
          className="inline-flex items-center gap-1.5 rounded border border-[var(--border-subtle)] bg-[var(--surface)] px-2.5 py-1 text-[0.7rem] font-medium text-[var(--muted)]"
        >
          {badge.key === "official-source" || badge.key === "field-checked" ? (
            <svg
              viewBox="0 0 24 24"
              width="12"
              height="12"
              fill="none"
              stroke="var(--cta)"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              className="shrink-0"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          ) : null}
          <span className="text-[var(--foreground)]">{badge.label}</span>
          {badge.detail ? <span className="opacity-75">· {badge.detail}</span> : null}
        </li>
      ))}
    </ul>
  );
}
